import { useTranslation } from "react-i18next";import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/shared/components/ui/card';
import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import { Badge } from '@/shared/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/shared/components/ui/tabs';
import { Progress } from '@/shared/components/ui/progress';
import { toast } from 'sonner';
import {
  Mail,
  Plus,
  Search,
  Send,
  Users,
  FileText,
  BarChart3,
  Clock,
  CheckCircle,
  AlertCircle,
  Eye,
  MousePointer,
  TrendingUp,
  Settings,
  Edit,
  Copy,
  Trash2,
  Play,
  Pause } from
'lucide-react';

// Mock data
const mockLists = [
{ id: 1, name: 'Newsletter Subscribers', subscribers: 12500, growth: 5.2, openRate: 24.5, clickRate: 3.8 },
{ id: 2, name: 'VIP Customers', subscribers: 890, growth: 12.1, openRate: 42.3, clickRate: 8.2 },
{ id: 3, name: 'Product Updates', subscribers: 5600, growth: 3.4, openRate: 31.2, clickRate: 5.6 },
{ id: 4, name: 'Abandoned Cart', subscribers: 2340, growth: 8.9, openRate: 28.7, clickRate: 12.4 }];


const mockCampaigns = [
{ id: 1, name: 'January Newsletter', status: 'sent', sent: 12500, opened: 3125, clicked: 478, date: '2025-01-15' },
{ id: 2, name: 'Flash Sale Announcement', status: 'sent', sent: 8900, opened: 2848, clicked: 534, date: '2025-01-12' },
{ id: 3, name: 'Product Launch', status: 'scheduled', scheduled: '2025-01-20 09:00', recipients: 12500 },
{ id: 4, name: 'Weekly Digest', status: 'draft', recipients: 5600 }];


const mockTemplates = [
{ id: 1, name: 'Newsletter Template', type: 'marketing', lastUsed: '2025-01-15' },
{ id: 2, name: 'Welcome Email', type: 'automation', lastUsed: '2025-01-14' },
{ id: 3, name: 'Sale Announcement', type: 'marketing', lastUsed: '2025-01-12' },
{ id: 4, name: 'Cart Reminder', type: 'automation', lastUsed: '2025-01-10' }];


const statusColors = {
  sent: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  scheduled: 'bg-blue-100 text-blue-700 border-blue-200',
  draft: 'bg-gray-100 text-gray-700 border-gray-200',
  active: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  paused: 'bg-amber-100 text-amber-700 border-amber-200'
};

export const EmailMarketing = () => {const { t } = useTranslation("cns");
  const [activeTab, setActiveTab] = useState('campaigns');
  const [searchQuery, setSearchQuery] = useState('');

  return (
    <div className="space-y-6" data-testid="email-marketing">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-3 font-['Space_Grotesk']">
            <div className="p-2 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-xl">
              <Mail className="h-7 w-7 text-white" />
            </div>{t("emailMarketing", "Email Marketing")}

          </h1>
          <p className="text-muted-foreground mt-1">{t("createAndManage", "Create and manage email campaigns, lists, and automations")}

          </p>
        </div>
        <Button className="gap-2 bg-gradient-to-r from-cyan-500 to-blue-600">
          <Plus className="h-4 w-4" />{t("newCampaign", "New Campaign")}

        </Button>
      </div>

      {/* Stats */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card className="card-elevated">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">{t("totalSubscribers", "Total Subscribers")}</p>
                <p className="text-2xl font-bold">{t("213k", "21.3K")}</p>
                <p className="text-xs text-emerald-600 flex items-center mt-1">
                  <TrendingUp className="h-3 w-3 mr-1" />{t("52ThisMonth", "+5.2% this month")}
                </p>
              </div>
              <div className="p-2 bg-cyan-100 rounded-lg">
                <Users className="h-5 w-5 text-cyan-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="card-elevated">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">{t("avgOpenRate", "Avg. Open Rate")}</p>
                <p className="text-2xl font-bold">{t("284", "28.4%")}</p>
                <p className="text-xs text-muted-foreground mt-1">{t("industryAvg213", "Industry avg: 21.3%")}</p>
              </div>
              <div className="p-2 bg-blue-100 rounded-lg">
                <Eye className="h-5 w-5 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="card-elevated">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">{t("avgClickRate", "Avg. Click Rate")}</p>
                <p className="text-2xl font-bold">{t("62", "6.2%")}</p>
                <p className="text-xs text-muted-foreground mt-1">{t("industryAvg26", "Industry avg: 2.6%")}</p>
              </div>
              <div className="p-2 bg-indigo-100 rounded-lg">
                <MousePointer className="h-5 w-5 text-indigo-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="card-elevated">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">{t("emailsSent", "Emails Sent")}</p>
                <p className="text-2xl font-bold">{t("452k", "45.2K")}</p>
                <p className="text-xs text-muted-foreground mt-1">{t("thisMonth", "This month")}</p>
              </div>
              <div className="p-2 bg-emerald-100 rounded-lg">
                <Send className="h-5 w-5 text-emerald-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="campaigns" className="gap-2">
            <Send className="h-4 w-4" />{t("campaigns", "Campaigns")}
          </TabsTrigger>
          <TabsTrigger value="lists" className="gap-2">
            <Users className="h-4 w-4" />{t("lists", "Lists")}
          </TabsTrigger>
          <TabsTrigger value="templates" className="gap-2">
            <FileText className="h-4 w-4" />{t("templates", "Templates")}
          </TabsTrigger>
          <TabsTrigger value="automations" className="gap-2">
            <Settings className="h-4 w-4" />{t("automations", "Automations")}
          </TabsTrigger>
        </TabsList>

        {/* Campaigns Tab */}
        <TabsContent value="campaigns" className="space-y-4 mt-4">
          <div className="flex gap-4">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input placeholder="Search campaigns..." className="pl-10" />
            </div>
          </div>

          <div className="space-y-3">
            {mockCampaigns.map((campaign) =>
            <Card key={campaign.id} className="card-elevated">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="p-2 bg-cyan-100 rounded-lg">
                        <Mail className="h-5 w-5 text-cyan-600" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="font-semibold">{campaign.name}</h3>
                          <Badge className={statusColors[campaign.status]}>
                            {campaign.status}
                          </Badge>
                        </div>
                        {campaign.status === 'sent' &&
                      <p className="text-sm text-muted-foreground">{t("sent", "Sent")}
                        {new Date(campaign.date).toLocaleDateString()} • {campaign.sent.toLocaleString()}{t("recipients", "recipients")}
                      </p>
                      }
                        {campaign.status === 'scheduled' &&
                      <p className="text-sm text-muted-foreground">{t("scheduledFor", "Scheduled for")}
                        {campaign.scheduled} • {campaign.recipients.toLocaleString()}{t("recipients", "recipients")}
                      </p>
                      }
                        {campaign.status === 'draft' &&
                      <p className="text-sm text-muted-foreground">
                            {campaign.recipients.toLocaleString()}{t("recipientsSelected", "recipients selected")}
                      </p>
                      }
                      </div>
                    </div>

                    {campaign.status === 'sent' &&
                  <div className="flex items-center gap-6">
                        <div className="text-center">
                          <p className="text-lg font-bold">{(campaign.opened / campaign.sent * 100).toFixed(1)}%</p>
                          <p className="text-xs text-muted-foreground">{t("openRate", "Open Rate")}</p>
                        </div>
                        <div className="text-center">
                          <p className="text-lg font-bold">{(campaign.clicked / campaign.sent * 100).toFixed(1)}%</p>
                          <p className="text-xs text-muted-foreground">{t("clickRate", "Click Rate")}</p>
                        </div>
                        <Button variant="outline" size="sm">{t("viewReport", "View Report")}</Button>
                      </div>
                  }

                    {campaign.status !== 'sent' &&
                  <div className="flex gap-2">
                        <Button variant="outline" size="sm">{t("edit", "Edit")}</Button>
                        {campaign.status === 'scheduled' &&
                    <Button variant="outline" size="sm">
                            <Pause className="h-4 w-4" />
                          </Button>
                    }
                        {campaign.status === 'draft' &&
                    <Button size="sm" className="bg-cyan-500 hover:bg-cyan-600">{t("schedule", "Schedule")}</Button>
                    }
                      </div>
                  }
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>

        {/* Lists Tab */}
        <TabsContent value="lists" className="space-y-4 mt-4">
          <div className="flex justify-between items-center">
            <div className="relative max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input placeholder="Search lists..." className="pl-10" />
            </div>
            <Button className="gap-2">
              <Plus className="h-4 w-4" />{t("newList", "New List")}
            </Button>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            {mockLists.map((list) =>
            <Card key={list.id} className="card-elevated">
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg">{list.name}</CardTitle>
                    <Button variant="ghost" size="icon" className="h-8 w-8">
                      <Settings className="h-4 w-4" />
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div>
                      <p className="text-2xl font-bold">{list.subscribers.toLocaleString()}</p>
                      <p className="text-xs text-muted-foreground flex items-center gap-1">
                        <Users className="h-3 w-3" />{t("subscribers", "Subscribers")}
                    </p>
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-emerald-600">+{list.growth}%</p>
                      <p className="text-xs text-muted-foreground flex items-center gap-1">
                        <TrendingUp className="h-3 w-3" />{t("growth", "Growth")}
                    </p>
                    </div>
                  </div>
                  <div className="flex gap-4 text-sm">
                    <div className="flex items-center gap-1">
                      <Eye className="h-3 w-3 text-muted-foreground" />
                      <span>{list.openRate}{t("open", "% open")}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <MousePointer className="h-3 w-3 text-muted-foreground" />
                      <span>{list.clickRate}{t("click", "% click")}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>

        {/* Templates Tab */}
        <TabsContent value="templates" className="space-y-4 mt-4">
          <div className="flex justify-between items-center">
            <div className="relative max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input placeholder="Search templates..." className="pl-10" />
            </div>
            <Button className="gap-2">
              <Plus className="h-4 w-4" />{t("newTemplate", "New Template")}
            </Button>
          </div>

          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {mockTemplates.map((template) =>
            <Card key={template.id} className="card-elevated hover:shadow-lg transition-shadow cursor-pointer">
                <CardContent className="p-4">
                  <div className="aspect-video bg-gradient-to-br from-cyan-50 to-blue-50 rounded-lg mb-3 flex items-center justify-center">
                    <FileText className="h-8 w-8 text-cyan-400" />
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-semibold">{template.name}</h3>
                      <p className="text-xs text-muted-foreground">{t("lastUsed", "Last used")}
                      {new Date(template.lastUsed).toLocaleDateString()}
                      </p>
                    </div>
                    <Badge variant="outline">{template.type}</Badge>
                  </div>
                  <div className="flex gap-2 mt-3">
                    <Button variant="outline" size="sm" className="flex-1 gap-1">
                      <Edit className="h-3 w-3" />{t("edit", "Edit")}
                  </Button>
                    <Button variant="outline" size="sm" className="gap-1">
                      <Copy className="h-3 w-3" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>

        {/* Automations Tab */}
        <TabsContent value="automations" className="space-y-4 mt-4">
          <Card className="card-elevated border-dashed">
            <CardContent className="p-12 text-center">
              <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-gradient-to-br from-cyan-100 to-blue-100 flex items-center justify-center">
                <Settings className="h-10 w-10 text-cyan-600" />
              </div>
              <h2 className="text-2xl font-bold mb-2">{t("emailAutomations", "Email Automations")}</h2>
              <p className="text-muted-foreground mb-6 max-w-md mx-auto">{t("createAutomatedEmail", "Create automated email workflows for welcome series, abandoned carts, and more.")}

              </p>
              <Button className="gap-2 bg-gradient-to-r from-cyan-500 to-blue-600">
                <Plus className="h-4 w-4" />{t("createAutomation", "Create Automation")}

              </Button>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>);

};

export default EmailMarketing;